'use client';

import type { CSSProperties } from 'react';
import { Box, token } from '@terra/ui-styled-system';
import {
  AlertCircleIcon,
  CheckmarkCircle01Icon,
  Loading03Icon,
  Alert01Icon,
  type TerraIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Tooltip } from '../../components/tooltip';
import {
  WAF_STATUS,
  type WafStatusForDomain,
} from '../domain-manager/waf-status';

type DomainWafStatusIconProps = {
  wafStatus: WafStatusForDomain;
};

type DomainWafStatusMessageKey =
  | 'verificationInProgress'
  | 'noWafDetected'
  | 'wafDetected'
  | 'urlUnreachable'
  | 'untrustedCertificate'
  | 'verificationError';

type DomainWafStatusDisplay = {
  messageKey: DomainWafStatusMessageKey;
  Icon: TerraIcon;
  color?: string;
  style?: CSSProperties;
};

const DISPLAY_BY_WAF_STATUS: Record<
  Exclude<WafStatusForDomain, typeof WAF_STATUS.idle>,
  DomainWafStatusDisplay
> = {
  [WAF_STATUS.pending]: {
    messageKey: 'verificationInProgress',
    Icon: Loading03Icon,
    style: { animation: 'spin 1s linear infinite' },
  },
  [WAF_STATUS.noWaf]: {
    messageKey: 'noWafDetected',
    Icon: CheckmarkCircle01Icon,
    color: token('colors.fg.neutral.success'),
  },
  [WAF_STATUS.wafDetected]: {
    messageKey: 'wafDetected',
    Icon: Alert01Icon,
    color: token('colors.fg.neutral.error'),
  },
  [WAF_STATUS.unreachable]: {
    messageKey: 'urlUnreachable',
    Icon: AlertCircleIcon,
  },
  [WAF_STATUS.untrustedCertificate]: {
    messageKey: 'untrustedCertificate',
    Icon: Alert01Icon,
    color: token('colors.fg.neutral.warning'),
  },
  [WAF_STATUS.error]: {
    messageKey: 'verificationError',
    Icon: AlertCircleIcon,
  },
};

export const DomainWafStatusIcon = ({
  wafStatus,
}: DomainWafStatusIconProps) => {
  const t = useTranslations('designSystem.domainScope.wafStatus');

  if (wafStatus === WAF_STATUS.idle) {
    return null;
  }

  const { messageKey, Icon, color, style } = DISPLAY_BY_WAF_STATUS[wafStatus];

  return (
    <Tooltip title={t(messageKey)} positioning={{ strategy: 'fixed' }}>
      <Box display="inline-flex">
        <Icon size={14} aria-hidden color={color} style={style} />
      </Box>
    </Tooltip>
  );
};
